import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { priceFormatter } from "../../utils/utils";

const H = 260;
const PAD_L = 56;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 34;

// zaokruži max na "lepu" vrednost (1/2/5 × 10^n)
const niceCeil = (v) => {
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(v, 1))));
  const n = v / pow;
  const f = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return f * pow;
};

const kFormat = (v) =>
  v >= 1000
    ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
    : `${Math.round(v)}`;

// "2026-07-14" -> "14.07."
const fmtDate = (d) => {
  if (!d) return "";
  const s = String(d);
  return s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.` : s;
};

// Catmull-Rom -> bezier, sa clamp-om da kriva ne probija pod grafikona
const smoothPath = (pts, yMin, yMax) => {
  if (pts.length < 2) return "";
  const cl = (y) => Math.min(Math.max(y, yMin), yMax);
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = cl(p1.y + (p2.y - p0.y) / 6);
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = cl(p2.y - (p3.y - p1.y) / 6);
    d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
  }
  return d;
};

const RevenueChart = ({ perDay }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) =>
      setWidth(entries[0].contentRect.width)
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geom = useMemo(() => {
    if (!width || !perDay?.length) return null;

    const values = perDay.map((d) => d.revenue);
    const max = niceCeil(Math.max(...values, 1));
    const innerW = width - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;
    const bottom = PAD_T + innerH;
    const step = innerW / (values.length - 1 || 1);

    const points = values.map((v, i) => ({
      x: PAD_L + i * step,
      y: bottom - (v / max) * innerH,
      revenue: v,
      date: perDay[i].date,
    }));

    const line = smoothPath(points, PAD_T, bottom);
    const area = `${line} L ${points[points.length - 1].x},${bottom} L ${PAD_L},${bottom} Z`;

    const grid = [0, 1 / 3, 2 / 3, 1].map((f) => ({
      y: bottom - f * innerH,
      label: kFormat(max * f),
    }));

    // x labele: ~6 ravnomerno raspoređenih datuma
    const every = Math.max(1, Math.round(points.length / 6));
    const xLabels = points.filter(
      (_, i) => i % every === 0 || i === points.length - 1
    );

    return { points, line, area, grid, xLabels, bottom, step };
  }, [width, perDay]);

  const handleMove = (e) => {
    if (!geom) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    let best = 0;
    let bestDist = Infinity;
    geom.points.forEach((p, i) => {
      const d = Math.abs(p.x - x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setHover(best);
  };

  if (!perDay?.length) return null;

  const active = hover != null && geom ? geom.points[hover] : null;

  // tooltip ne sme van ivica
  const tipAlign =
    active && width
      ? active.x < 90
        ? "left"
        : active.x > width - 90
        ? "right"
        : "center"
      : "center";

  return (
    <Wrap
      ref={wrapRef}
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      onMouseLeave={() => setHover(null)}
      onTouchEnd={() => setHover(null)}
    >
      {geom && (
        <svg width={width} height={H}>
          <defs>
            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fe5f55" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#fe5f55" stopOpacity="0" />
            </linearGradient>
          </defs>

          {geom.grid.map((g, i) => (
            <g key={i}>
              <line
                x1={PAD_L}
                x2={width - PAD_R}
                y1={g.y}
                y2={g.y}
                stroke="rgba(255,255,255,0.06)"
                strokeDasharray={i === 0 ? "0" : "3 5"}
              />
              <GridLabel as="text" x={PAD_L - 10} y={g.y + 4} textAnchor="end">
                {g.label}
              </GridLabel>
            </g>
          ))}

          {geom.xLabels.map((p, i) => (
            <GridLabel
              as="text"
              key={i}
              x={p.x}
              y={H - 10}
              textAnchor="middle"
            >
              {fmtDate(p.date)}
            </GridLabel>
          ))}

          <path d={geom.area} fill="url(#revGrad)" />
          <AnimatedLine
            d={geom.line}
            fill="none"
            stroke="#fe5f55"
            strokeWidth="2"
            strokeLinecap="round"
            pathLength="1"
          />

          {active && (
            <line
              x1={active.x}
              x2={active.x}
              y1={PAD_T}
              y2={geom.bottom}
              stroke="rgba(254,95,85,0.35)"
              strokeDasharray="3 4"
            />
          )}

          {geom.points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={hover === i ? 5 : 2.5}
              fill={hover === i ? "#0a0a0c" : "#fe5f55"}
              stroke="#fe5f55"
              strokeWidth={hover === i ? 2.5 : 0}
              style={{ transition: "r 0.15s" }}
            />
          ))}
        </svg>
      )}

      {active && (
        <Tooltip
          style={{ left: active.x, top: active.y }}
          $align={tipAlign}
        >
          <TipDate>{fmtDate(active.date)}</TipDate>
          <TipValue>
            {priceFormatter(active.revenue)} <span>RSD</span>
          </TipValue>
        </Tooltip>
      )}
    </Wrap>
  );
};

export default RevenueChart;

const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

const Wrap = styled.div`
  position: relative;
  width: 100%;
  height: ${H}px;
  cursor: crosshair;

  svg {
    display: block;
  }
`;

const AnimatedLine = styled.path`
  stroke-dasharray: 1;
  animation: ${draw} 1.2s var(--ease, ease) forwards;
`;

const GridLabel = styled.text`
  font-family: "Poppins-Medium", sans-serif;
  font-size: 1.05rem;
  fill: var(--muted2, #5c5a63);
  letter-spacing: 0.04em;
`;

const Tooltip = styled.div`
  position: absolute;
  transform: ${({ $align }) =>
    $align === "left"
      ? "translate(0, calc(-100% - 14px))"
      : $align === "right"
      ? "translate(-100%, calc(-100% - 14px))"
      : "translate(-50%, calc(-100% - 14px))"};
  background: var(--bg3, #1e1e24);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.5rem;
  padding: 0.8rem 1.1rem;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  z-index: 2;
`;

const TipDate = styled.div`
  font-family: "Poppins-Medium";
  font-size: 1.05rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
  margin-bottom: 0.3rem;
`;

const TipValue = styled.div`
  font-family: "Anton", sans-serif;
  font-size: 1.7rem;
  line-height: 1;
  color: var(--text, #f4f2ee);

  span {
    font-size: 1.1rem;
    color: var(--red, #fe5f55);
    font-family: "Poppins-SemiBold";
  }
`;
